import trimCanvas from 'trim-canvas';

/**
 * Canonical dimensions for typed signatures. Same logic for manual sign (per-field) and
 * instant sign / sign all (no field)
 */
const CANONICAL_WIDTH = 400;
const CANONICAL_HEIGHT = 120;
const BASE_FONT_SIZE = 32;
const PADDING_RATIO = 0.08;
const MIN_FONT_SIZE = 8;
const FONT_SIZE_STEP = 2;
const FILL_STYLE = '#556EE6';

/**
 * Creates a signature image from typed text using a fixed canvas size and font.
 * Works the same on all devices and for all flows (manual field, sign all, instant sign).
 * Caller / backend scales the result when placing into fields of different sizes.
 *
 * @param {string} text - The signature text (e.g. full name or initials).
 * @param {string} fontFamily - Font family string (e.g. from getComputedStyle: "Satisfy, cursive").
 * @param {Object} options - Optional overrides.
 * @param {number} options.width - Logical canvas width (default CANONICAL_WIDTH).
 * @param {number} options.height - Logical canvas height (default CANONICAL_HEIGHT).
 * @param {number} options.baseFontSize - Base font size (default BASE_FONT_SIZE).
 * @returns {string} Data URL (image/png) of the signature.
 */
export function createSignatureImageFromText(text: string, fontFamily: string, options: { width?: number, height?: number, baseFontSize?: number } = {}): string {
  const width = options.width ?? CANONICAL_WIDTH;
  const height = options.height ?? CANONICAL_HEIGHT;
  const baseFontSize = options.baseFontSize ?? BASE_FONT_SIZE;

  // Device pixel ratio: 1 on standard screens, 2–3 on retina/mobile.
  // We scale the canvas by this so the bitmap has enough pixels and doesn't look blurry when displayed.
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  // Use at least 2x scale so the trimmed image still has enough pixels when shown at field size (avoids blur on mobile).
  const scale = Math.max(dpr, 2);

  // Padding around the text: PADDING_RATIO (8%) of height, but at least 2px so small heights still have a minimal margin.
  const padding = Math.max(2, height * PADDING_RATIO);
  // Bounds for the text so it stays inside the canvas (width and height minus padding on both sides).
  const maxTextWidth = width - padding * 2;
  const maxTextHeight = height - padding * 2;

  const canvas = document.createElement('canvas');
  // Canvas size in pixels: logical size × scale so the exported image is high-res.
  canvas.width = width * scale;
  canvas.height = height * scale;

  const ctx: any = canvas.getContext('2d');
  // Scale the context so all drawing is in logical coordinates
  ctx.scale(scale, scale);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillStyle = FILL_STYLE;

  // Initial font size: baseFontSize or maxTextHeight, whichever is smaller, so the text isn't too tall.
  let fontSize = Math.min(baseFontSize, maxTextHeight);
  ctx.font = `${fontSize}px ${fontFamily}`;
  // If the text is too wide, reduce font size in FONT_SIZE_STEP (2px) steps. Stop at MIN_FONT_SIZE (8px) so we don't go too small to read.
  while (ctx.measureText(text).width > maxTextWidth && fontSize > MIN_FONT_SIZE) {
    fontSize -= FONT_SIZE_STEP;
    ctx.font = `${fontSize}px ${fontFamily}`;
  }

  ctx.fillText(text, padding, padding);
  trimCanvas(canvas);

  return canvas.toDataURL('image/png');
}
